import { useMemo, useState } from 'react';
import {
  BookOpen,
  Bug,
  CalendarRange,
  ExternalLink,
  Filter,
  GitBranch,
  Network,
  Search,
  Shield,
  Target,
} from 'lucide-react';

interface Phase3GraphNode {
  id: string;
  kind: 'actor' | 'campaign' | 'technique' | 'software' | 'cve';
  label: string;
  secondary: string;
  description: string;
}

interface Phase3GraphEdge {
  id: string;
  source: string;
  target: string;
  relationship: string;
  derived: boolean;
  description: string;
}

interface RelatedActor {
  id: string;
  name: string;
  mitreId: string;
  nation: string;
  confidence: 'direct_stix' | 'source_report_context';
  matchedBy: string[];
  reports: Array<{ id: string; title: string; url: string; publisher: string; date: string | null }>;
  graph: {
    actor: { id: string; name: string; mitreId: string };
    nodes: Phase3GraphNode[];
    edges: Phase3GraphEdge[];
    summary: { campaigns: number; techniques: number; software: number; cves: number; nodes: number; edges: number; truncated: boolean };
  } | null;
}

interface RelationshipsResponse {
  ok: true;
  value: string;
  directStixIndicatorIds: string[];
  actors: RelatedActor[];
  reports: Array<{ id: string; title: string; url: string; publisher: string; date: string | null }>;
  searchedAt: string;
  note: string;
}

interface IndicatorLike {
  value: string;
  type: string;
  source: string;
}

type NodeKind = 'indicator' | 'actor' | 'campaign' | 'technique' | 'software' | 'cve' | 'source';

interface GraphNode {
  id: string;
  kind: NodeKind;
  label: string;
  secondary: string;
  description: string;
  actorId?: string;
  url?: string;
}

interface GraphEdge {
  id: string;
  source: string;
  target: string;
  relationship: string;
  derived: boolean;
  description: string;
}

const W = 1180;
const H = 700;
const CX = W / 2;
const CY = H / 2;
const MAX_NODES = 120;
const MAX_EDGES = 220;

function short(value: string, max = 72): string { return value.length <= max ? value : `${value.slice(0, max - 1)}…`; }
function fmt(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
function kindLabel(kind: NodeKind): string {
  if (kind === 'indicator') return 'Indicator';
  if (kind === 'actor') return 'Threat Actor';
  if (kind === 'campaign') return 'Campaign';
  if (kind === 'technique') return 'ATT&CK / TTP';
  if (kind === 'software') return 'Malware / Tool';
  if (kind === 'cve') return 'CVE';
  return 'Source';
}
function kindStyle(kind: NodeKind): { fill: string; stroke: string; text: string } {
  if (kind === 'indicator') return { fill: '#083344', stroke: '#22d3ee', text: '#ecfeff' };
  if (kind === 'actor') return { fill: '#2e1065', stroke: '#a78bfa', text: '#f5f3ff' };
  if (kind === 'campaign') return { fill: '#3b0764', stroke: '#c084fc', text: '#faf5ff' };
  if (kind === 'technique') return { fill: '#082f49', stroke: '#38bdf8', text: '#e0f2fe' };
  if (kind === 'software') return { fill: '#052e16', stroke: '#34d399', text: '#ecfdf5' };
  if (kind === 'cve') return { fill: '#451a03', stroke: '#fbbf24', text: '#fffbeb' };
  return { fill: '#1e293b', stroke: '#94a3b8', text: '#f8fafc' };
}
function edgeTone(relationship: string, derived: boolean): string {
  if (derived) return '#f59e0b';
  const value = relationship.toLowerCase();
  if (value.includes('direct')) return '#34d399';
  if (value.includes('use')) return '#34d399';
  if (value.includes('campaign')) return '#c084fc';
  if (value.includes('vulnerab') || value.includes('cve')) return '#fbbf24';
  return '#38bdf8';
}

function ringPositions(count: number, radius: number, startDeg = -90): Array<{ x: number; y: number }> {
  if (!count) return [];
  return Array.from({ length: count }, (_, index) => {
    const angle = ((startDeg + (360 * index) / count) * Math.PI) / 180;
    return { x: CX + Math.cos(angle) * radius, y: CY + Math.sin(angle) * radius };
  });
}

function sectorPositions(count: number, startDeg: number, endDeg: number, inner = 245, outer = 330): Array<{ x: number; y: number }> {
  if (!count) return [];
  const first = Math.ceil(count / 2);
  return Array.from({ length: count }, (_, index) => {
    const outerRing = index >= first;
    const ringIndex = outerRing ? index - first : index;
    const ringCount = outerRing ? count - first : first;
    const t = ringCount <= 1 ? 0.5 : ringIndex / (ringCount - 1);
    const degree = startDeg + (endDeg - startDeg) * t;
    const angle = (degree * Math.PI) / 180;
    const radius = outerRing ? outer : inner;
    return { x: CX + Math.cos(angle) * radius, y: CY + Math.sin(angle) * radius };
  });
}

function layout(nodes: GraphNode[]): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();
  const indicator = nodes.find((node) => node.kind === 'indicator');
  if (indicator) positions.set(indicator.id, { x: CX, y: CY });

  const actors = nodes.filter((node) => node.kind === 'actor');
  ringPositions(actors.length, actors.length <= 4 ? 150 : 175).forEach((point, index) => positions.set(actors[index].id, point));

  const sectors: Array<{ kind: NodeKind; start: number; end: number }> = [
    { kind: 'campaign', start: 200, end: 275 },
    { kind: 'software', start: 280, end: 355 },
    { kind: 'cve', start: 5, end: 82 },
    { kind: 'technique', start: 88, end: 165 },
    { kind: 'source', start: 170, end: 198 },
  ];
  for (const sector of sectors) {
    const members = nodes.filter((node) => node.kind === sector.kind);
    const points = sectorPositions(members.length, sector.start, sector.end);
    members.forEach((node, index) => positions.set(node.id, points[index] ?? { x: CX, y: CY }));
  }
  return positions;
}

function curvedPath(source: { x: number; y: number }, target: { x: number; y: number }): string {
  const mx = (source.x + target.x) / 2;
  const my = (source.y + target.y) / 2;
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const len = Math.max(1, Math.hypot(dx, dy));
  const bend = Math.min(34, len * 0.085);
  const cx = mx - (dy / len) * bend;
  const cy = my + (dx / len) * bend;
  return `M ${source.x.toFixed(1)} ${source.y.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${target.x.toFixed(1)} ${target.y.toFixed(1)}`;
}

function buildGraph(response: RelationshipsResponse, indicator: IndicatorLike): { nodes: GraphNode[]; edges: GraphEdge[]; truncated: boolean } {
  const nodeMap = new Map<string, GraphNode>();
  const edges: GraphEdge[] = [];
  const central: GraphNode = { id: 'indicator::root', kind: 'indicator', label: indicator.value, secondary: indicator.type, description: `Customer ThreatIntelIndicators object · ${indicator.source || 'Microsoft Sentinel'}` };
  nodeMap.set(central.id, central);

  const actorNodeIds = new Map<string, string>();
  for (const actor of response.actors.slice(0, 12)) {
    const actorNodeId = `actor::${actor.id}`;
    actorNodeIds.set(actor.id, actorNodeId);
    nodeMap.set(actorNodeId, { id: actorNodeId, kind: 'actor', label: actor.name, secondary: `${actor.nation || 'Unknown'} · ${actor.mitreId || 'MITRE ID unavailable'}`, description: actor.matchedBy.length ? `Matched by ${actor.matchedBy.join(', ')}` : 'Source-backed actor relationship.', actorId: actor.id });
    edges.push({ id: `indicator-to-${actor.id}`, source: central.id, target: actorNodeId, relationship: actor.confidence === 'direct_stix' ? 'Direct STIX relationship' : 'Source-report context', derived: actor.confidence !== 'direct_stix', description: actor.confidence === 'direct_stix' ? 'The synchronized STIX dataset contains a source-backed relationship path between this indicator and actor.' : 'The IoC and actor occur in common source-report context; this is not treated as confirmed attribution.' });

    const graph = actor.graph;
    if (!graph) continue;
    const localMap = new Map<string, string>();
    const graphActor = graph.nodes.find((node) => node.kind === 'actor');
    if (graphActor) localMap.set(graphActor.id, actorNodeId);

    for (const node of graph.nodes) {
      if (node.kind === 'actor') continue;
      const nodeId = `${actor.id}::${node.id}`;
      localMap.set(node.id, nodeId);
      if (!nodeMap.has(nodeId)) nodeMap.set(nodeId, { id: nodeId, kind: node.kind, label: node.label, secondary: node.secondary, description: node.description, actorId: actor.id });
      if (nodeMap.size >= MAX_NODES) break;
    }

    for (const edge of graph.edges) {
      const source = localMap.get(edge.source);
      const target = localMap.get(edge.target);
      if (!source || !target || source === target) continue;
      edges.push({ id: `${actor.id}::${edge.id}`, source, target, relationship: edge.relationship, derived: edge.derived, description: edge.description });
      if (edges.length >= MAX_EDGES) break;
    }
    if (nodeMap.size >= MAX_NODES || edges.length >= MAX_EDGES) break;
  }

  for (const report of response.reports.slice(0, 10)) {
    if (nodeMap.size >= MAX_NODES) break;
    const reportId = `source::${report.id}`;
    nodeMap.set(reportId, { id: reportId, kind: 'source', label: report.title || report.publisher || 'Source report', secondary: `${report.publisher || 'Source'} · ${fmt(report.date)}`, description: 'Source report associated with this indicator relationship context.', url: report.url });
    edges.push({ id: `indicator-source-${report.id}`, source: central.id, target: reportId, relationship: 'Source context', derived: true, description: 'Source report context for this indicator. It is not itself proof of actor attribution.' });
  }

  return { nodes: Array.from(nodeMap.values()).slice(0, MAX_NODES), edges: edges.slice(0, MAX_EDGES), truncated: nodeMap.size >= MAX_NODES || edges.length >= MAX_EDGES };
}

export function IndicatorLandscapeIntelligenceGraph({ response, indicator, onOpenActor }: { response: RelationshipsResponse; indicator: IndicatorLike; onOpenActor?: (actorId: string) => void }) {
  const graph = useMemo(() => buildGraph(response, indicator), [indicator, response]);
  const [search, setSearch] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState('indicator::root');
  const [enabledKinds, setEnabledKinds] = useState<Set<NodeKind>>(new Set(['indicator', 'actor', 'campaign', 'technique', 'software', 'cve', 'source']));
  const needle = search.trim().toLowerCase();

  const visibleNodes = useMemo(() => graph.nodes.filter((node) => {
    if (node.kind === 'indicator') return true;
    if (!enabledKinds.has(node.kind)) return false;
    if (!needle) return true;
    return [node.label, node.secondary, node.description].some((value) => value.toLowerCase().includes(needle));
  }), [enabledKinds, graph.nodes, needle]);
  const visibleIds = useMemo(() => new Set(visibleNodes.map((node) => node.id)), [visibleNodes]);
  const visibleEdges = useMemo(() => graph.edges.filter((edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target)), [graph.edges, visibleIds]);
  const positions = useMemo(() => layout(visibleNodes), [visibleNodes]);
  const selectedNode = graph.nodes.find((node) => node.id === selectedNodeId) ?? graph.nodes[0];
  const connected = useMemo(() => {
    const set = new Set<string>(selectedNode ? [selectedNode.id] : []);
    if (!selectedNode) return set;
    visibleEdges.forEach((edge) => {
      if (edge.source === selectedNode.id) set.add(edge.target);
      if (edge.target === selectedNode.id) set.add(edge.source);
    });
    return set;
  }, [selectedNode, visibleEdges]);

  function toggleKind(kind: NodeKind) {
    if (kind === 'indicator') return;
    setEnabledKinds((current) => {
      const next = new Set(current);
      if (next.has(kind)) next.delete(kind); else next.add(kind);
      next.add('indicator');
      return next;
    });
  }

  const counts = useMemo(() => ({
    actors: graph.nodes.filter((node) => node.kind === 'actor').length,
    campaigns: graph.nodes.filter((node) => node.kind === 'campaign').length,
    techniques: graph.nodes.filter((node) => node.kind === 'technique').length,
    software: graph.nodes.filter((node) => node.kind === 'software').length,
    cves: graph.nodes.filter((node) => node.kind === 'cve').length,
    sources: graph.nodes.filter((node) => node.kind === 'source').length,
  }), [graph.nodes]);

  return (
    <div className="overflow-hidden rounded-2xl border border-violet-400/20 bg-gradient-to-br from-slate-950 via-slate-950 to-violet-950/25 shadow-xl">
      <div className="border-b border-white/10 px-5 py-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-violet-300"><GitBranch size={15} /> Phase 3.5D · Indicator Intelligence Graph</div>
            <h4 className="mt-2 text-xl font-black text-white">IoC ↔ actor ↔ campaign ↔ TTP ↔ malware/tool ↔ CVE</h4>
            <p className="mt-1 max-w-5xl text-xs leading-5 text-blue-100/60">The selected IoC is the center of a bounded deterministic graph assembled from synchronized STIX and source-report context. Solid paths represent direct/source-backed graph relationships; amber dashed paths are contextual/derived pivots and are not treated as confirmed attribution.</p>
          </div>
          <div className="grid grid-cols-3 gap-1.5 text-center text-[9px] sm:grid-cols-6">
            <Metric label="Actors" value={counts.actors} icon={Shield} />
            <Metric label="Campaigns" value={counts.campaigns} icon={CalendarRange} />
            <Metric label="TTPs" value={counts.techniques} icon={Target} />
            <Metric label="Software" value={counts.software} icon={Bug} />
            <Metric label="CVEs" value={counts.cves} icon={Network} />
            <Metric label="Sources" value={counts.sources} icon={BookOpen} />
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-3 flex flex-col gap-2 xl:flex-row xl:items-center">
          <label className="relative min-w-0 flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-100/35" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search actor, campaign, TTP, malware, CVE or source…" className="w-full rounded-xl border border-white/10 bg-slate-950/70 py-2.5 pl-9 pr-3 text-xs text-white outline-none focus:border-violet-400/50" /></label>
          <div className="flex flex-wrap gap-1.5"><span className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[9px] font-black text-blue-100/45"><Filter size={10} /> Layers</span>{(['actor', 'campaign', 'technique', 'software', 'cve', 'source'] as NodeKind[]).map((kind) => <button key={kind} type="button" onClick={() => toggleKind(kind)} className={`rounded-lg border px-2 py-1 text-[9px] font-black ${enabledKinds.has(kind) ? 'border-violet-400/25 bg-violet-500/10 text-violet-100' : 'border-white/10 bg-white/5 text-blue-100/35'}`}>{kindLabel(kind)}</button>)}</div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#020817]">
            <svg viewBox={`0 0 ${W} ${H}`} className="block aspect-[59/35] w-full select-none">
              <defs><filter id="ks-ioc-graph-glow"><feGaussianBlur stdDeviation="4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter></defs>
              {visibleEdges.map((edge) => {
                const source = positions.get(edge.source); const target = positions.get(edge.target);
                if (!source || !target) return null;
                const highlighted = selectedNode ? edge.source === selectedNode.id || edge.target === selectedNode.id : false;
                const tone = edgeTone(edge.relationship, edge.derived);
                return <g key={edge.id} pointerEvents="none"><path d={curvedPath(source, target)} fill="none" stroke={tone} strokeWidth={highlighted ? 4.8 : 2.6} opacity={highlighted ? 0.13 : 0.06} /><path d={curvedPath(source, target)} fill="none" stroke={tone} strokeWidth={highlighted ? 2.3 : 1.15} strokeDasharray={edge.derived ? '8 6' : undefined} opacity={highlighted ? 0.95 : 0.38}><title>{edge.relationship} · {edge.description}</title>{highlighted && edge.derived && <animate attributeName="stroke-dashoffset" from="0" to="-28" dur="2.3s" repeatCount="indefinite" />}</path></g>;
              })}
              {visibleNodes.map((node) => {
                const point = positions.get(node.id); if (!point) return null;
                const style = kindStyle(node.kind); const active = selectedNode?.id === node.id; const related = connected.has(node.id);
                const width = node.kind === 'indicator' ? 172 : node.kind === 'actor' ? 145 : 128;
                const height = node.kind === 'indicator' ? 54 : 46;
                return <g key={node.id} transform={`translate(${point.x},${point.y})`} className="cursor-pointer" onClick={() => setSelectedNodeId(node.id)} opacity={selectedNode && !related ? 0.36 : 1}>
                  {active && <circle r={44} fill={style.stroke} opacity="0.12" filter="url(#ks-ioc-graph-glow)" />}
                  <rect x={-width / 2} y={-height / 2} width={width} height={height} rx="12" fill={style.fill} stroke={style.stroke} strokeWidth={active ? 2.4 : 1.25} />
                  <text x="0" y="-3" textAnchor="middle" fontSize="10" fontWeight="800" fill={style.text}>{short(node.label, node.kind === 'indicator' ? 28 : 22)}</text>
                  <text x="0" y="12" textAnchor="middle" fontSize="7.5" fill={style.text} opacity="0.66">{short(node.secondary || kindLabel(node.kind), 28)}</text>
                  <title>{kindLabel(node.kind)} · {node.label} · {node.description}</title>
                </g>;
              })}
            </svg>
            <div className="pointer-events-none absolute bottom-3 left-3 rounded-xl border border-white/10 bg-slate-950/85 px-3 py-2 text-[9px] text-blue-100/50 backdrop-blur">Click a node to isolate its neighborhood · solid = direct graph relation · dashed amber = contextual/derived</div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            {selectedNode ? <>
              <div className="text-[10px] font-black uppercase tracking-[0.16em] text-violet-300">Selected node</div>
              <div className="mt-2 break-all text-base font-black text-white">{selectedNode.label}</div>
              <div className="mt-1 text-[10px] text-blue-100/50">{kindLabel(selectedNode.kind)} · {selectedNode.secondary || 'No secondary identifier'}</div>
              <div className="mt-3 rounded-xl border border-white/10 bg-slate-950/50 p-3 text-xs leading-5 text-blue-100/65">{selectedNode.description || 'No additional description is available for this synchronized object.'}</div>
              <div className="mt-3 text-[10px] font-black uppercase tracking-wide text-blue-100/40">Connected relationships</div>
              <div className="mt-2 max-h-64 space-y-1.5 overflow-auto">{visibleEdges.filter((edge) => edge.source === selectedNode.id || edge.target === selectedNode.id).map((edge) => <div key={edge.id} className="rounded-lg border border-white/10 bg-slate-950/40 px-2.5 py-2 text-[10px]"><div className="font-black text-blue-100">{edge.relationship}</div><div className="mt-0.5 text-blue-100/45">{edge.derived ? 'Contextual / derived pivot' : 'Direct graph relationship'}</div></div>)}{visibleEdges.every((edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id) && <div className="text-xs text-blue-100/40">No visible relationship after the current filters.</div>}</div>
              {selectedNode.kind === 'actor' && selectedNode.actorId && onOpenActor && <button type="button" onClick={() => onOpenActor(selectedNode.actorId!)} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-violet-400/25 bg-violet-500/10 px-3 py-2 text-xs font-black text-violet-100"><GitBranch size={13} /> Open actor Phase 3 graph</button>}
              {selectedNode.url && <a href={selectedNode.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-cyan-400/25 bg-cyan-500/10 px-3 py-2 text-xs font-black text-cyan-100"><ExternalLink size={13} /> Open source</a>}
            </> : <div className="text-sm text-blue-100/45">Select a graph node.</div>}
          </div>
        </div>

        {graph.truncated && <div className="mt-3 rounded-xl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-[10px] text-amber-100">This is a bounded analyst view. Additional synchronized relationships exist but were capped to protect browser performance.</div>}
      </div>
    </div>
  );
}

function Metric({ label, value, icon: Icon }: { label: string; value: number; icon: typeof Shield }) {
  return <div className="rounded-lg border border-white/10 bg-white/5 px-2 py-2"><Icon size={11} className="mx-auto text-violet-300" /><div className="mt-1 font-black text-white">{value}</div><div className="text-blue-100/40">{label}</div></div>;
}
